import './tooltip.scss';

import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';

type Props = {
  buttonText: string;
  buttonAccessibleLabel?: string;
  dataQa: string;
  className?: string;
  children: React.ReactNode;
};

export const Tooltip: FunctionComponent<Props> = ({
  buttonText,
  buttonAccessibleLabel,
  dataQa,
  children,
  className = '',
}) => {
  const { t } = useTranslation();
  const [isTooltipShowing, setIsTooltipShowing] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const setFocusOnButton = () => {
    if (buttonRef.current) {
      buttonRef.current.focus();
    }
  };

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (isTooltipShowing && e.code === 'Escape') {
        setIsTooltipShowing(false);
        setFocusOnButton();
      }
    },
    [isTooltipShowing],
  );

  const handleFocusOutsideTooltip = (e: Event) => {
    if (
      wrapperRef.current &&
      !wrapperRef.current.contains(e.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(e.target as Node)
    ) {
      setIsTooltipShowing(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleFocusOutsideTooltip);
    document.addEventListener('focusin', handleFocusOutsideTooltip);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleFocusOutsideTooltip);
      document.removeEventListener('focusin', handleFocusOutsideTooltip);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleEscape]);

  useEffect(() => {
    if (isTooltipShowing && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isTooltipShowing]);

  const toggleTooltip = (e: Event | React.MouseEvent) => {
    e.preventDefault();
    setIsTooltipShowing((previousIsTooltipShowing) => {
      if (previousIsTooltipShowing) {
        setFocusOnButton();
      }
      return !previousIsTooltipShowing;
    });
  };

  return (
    <div className={className}>
      <button
        ref={buttonRef}
        type="button"
        data-qa={`${dataQa}-tooltip-btn-toggle`}
        className="tooltip__button text-link-base text-center"
        onClick={toggleTooltip}
        aria-label={buttonAccessibleLabel}
        aria-expanded={isTooltipShowing}
      >
        {buttonText}
      </button>
      {isTooltipShowing && (
        <div className="tooltip__wrapper" ref={wrapperRef}>
          <div className="tooltip__container">
            <div
              role="dialog"
              aria-label={buttonAccessibleLabel || buttonText}
              className={'tooltip'}
              ref={contentRef}
              tabIndex={-1}
            >
              {children}
              <button
                type="button"
                data-qa={`${dataQa}-tooltip-btn-close`}
                className="tooltip__close"
                onClick={toggleTooltip}
                aria-label={t('common.close')}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
